import { z } from 'zod';
/**
 * The control library: what good security looks like for an SME, in 12 domains (CyberScore model v4.2).
 * Controls are configuration (kind "control", published by the platform); questions point at them through
 * `metadata.controls`, and scoring and findings read them.
 */
export const CONTROL_DOMAINS = [
    { code: 'GV', name: 'Governance & cyber risk management', weight: 7, functions: 'Govern' },
    { code: 'AM', name: 'Asset & software management', weight: 6, functions: 'Identify' },
    { code: 'IA', name: 'Identity & access management', weight: 12, functions: 'Protect' },
    { code: 'EP', name: 'Endpoint security', weight: 9, functions: 'Protect · Detect' },
    { code: 'NS', name: 'Network security', weight: 8, functions: 'Protect' },
    { code: 'VM', name: 'Vulnerability & patch management', weight: 10, functions: 'Identify · Protect' },
    { code: 'DP', name: 'Data protection & privacy', weight: 10, functions: 'Protect' },
    { code: 'EH', name: 'Email, phishing & human risk', weight: 8, functions: 'Protect' },
    { code: 'LD', name: 'Logging, detection & monitoring', weight: 8, functions: 'Detect' },
    { code: 'BR', name: 'Backup & ransomware resilience', weight: 10, functions: 'Recover' },
    { code: 'IR', name: 'Incident response & business continuity', weight: 7, functions: 'Respond · Recover' },
    { code: 'TP', name: 'Third-party & supply chain', weight: 5, functions: 'Govern' },
];
export const DOMAIN_CODES = CONTROL_DOMAINS.map((d) => d.code);
export const ControlDomain = z.enum(DOMAIN_CODES);
/** The strongest kind of evidence for a control, and how far it can be trusted (confidence, %). */
export const EVIDENCE_LEVELS = {
    api: { label: 'API / integration', confidence: 90 },
    scan: { label: 'External scan', confidence: 75 },
    config: { label: 'Screenshot / configuration', confidence: 60 },
    document: { label: 'Document', confidence: 40 },
    self: { label: 'Self-declared', confidence: 20 },
};
export const EvidenceLevel = z.enum(Object.keys(EVIDENCE_LEVELS));
/** Industry packs. The assessment context's `industry` uses these keys. */
export const INDUSTRIES = {
    manufacturing: 'Manufacturing',
    healthcare: 'Healthcare',
    retail_ecommerce: 'Retail & e-commerce',
    professional_services: 'Professional services',
    logistics: 'Logistics',
    financial_services: 'Financial services',
    // Task 81: sectors the Cyber Security Act 2024 names as critical, and education.
    energy: 'Energy, oil & gas',
    utilities: 'Utilities',
    telecoms: 'Telecommunications',
    government: 'Government & public sector',
    education: 'Education',
};
export const Industry = z.enum(Object.keys(INDUSTRIES));
/** Facts about a business's set-up that decide whether a common control applies. */
export const PROFILE_FLAGS = {
    staff_50_plus: 'Staff ≥ 50',
    cloud_email: 'Microsoft 365 / Google',
    own_servers: 'Own servers',
    remote_access: 'Remote access',
    website: 'Website / web app',
    cloud_iaas: 'Cloud (IaaS)',
    // Task 82: a financial firm licensed or approved by Bank Negara Malaysia, under RMiT in full.
    bnm_licensed: 'Licensed by BNM',
    // Task 83: a healthcare provider that consults patients remotely, by video or phone.
    telehealth: 'Telehealth',
    // Task 84: unmanned or offshore sites; an entity the government designated under the Cyber Security Act.
    remote_sites: 'Remote sites',
    ncii: 'NCII entity',
};
export const ProfileFlag = z.enum(Object.keys(PROFILE_FLAGS));
export const ControlDefinition = z.strictObject({
    name: z.string().trim().min(1).max(200),
    domain: ControlDomain,
    /** 1–10: how much the control reduces loss. Sets its weight within the domain. */
    criticality: z.number().int().min(1).max(10),
    best_evidence: EvidenceLevel,
    frameworks: z.strictObject({
        nist_csf: z.string().min(1).max(50),
        cis: z.string().max(50).nullable().default(null),
        iso_27001: z.string().max(50).nullable().default(null),
    }),
    regulation: z.string().max(200).nullable().default(null),
    /** Failing a kill-switch control caps the score and raises an underwriting exception. */
    kill_switch: z.boolean().default(false),
    /** Empty: every SME. An industry pack, or a profile fact such as running its own servers. */
    applies: z
        .strictObject({ industries: z.array(Industry).min(1).optional(), profile: ProfileFlag.optional() })
        .default({}),
    /** The plain-language question the library suggests for it. */
    question: z.string().trim().min(1).max(500),
});
//# sourceMappingURL=controls.js.map